import { useTranslation } from 'react-i18next';
import { HandHelping } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useRoundDelegation } from './round-delegation-context';

interface RoundHelperStripProps {
  /**
   * Whether the round still takes this phone's marks — the round's own
   * `can_write`, which moves with every refresh. When it does not, the strip
   * says whose job the cards are now.
   */
  canWrite: boolean;
  className?: string;
}

/**
 * "You're helping Mario R." — at the top of a round page open on a helper's
 * phone, so nobody holding it mistakes whose card they are writing. Nothing on
 * any other page.
 */
export function RoundHelperStrip({ canWrite, className }: RoundHelperStripProps) {
  const { t } = useTranslation();
  const helping = useRoundDelegation();
  if (!helping) return null;

  const name = helping.stored.grantorName;

  return (
    <div
      className={cn(
        'mb-3 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm',
        className
      )}
      data-testid="helper-strip"
    >
      <HandHelping className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium">{t('roundHelp.strip.helping', { name })}</p>
        {!canWrite && (
          <p className="text-xs text-muted-foreground" data-testid="helper-read-only">
            {t('roundHelp.strip.readOnly', { name })}
          </p>
        )}
      </div>
    </div>
  );
}
